// src/services/conciliationApi.js
import axios from 'axios';
import { apiMainUrl } from '../apiConfig';

const withAuth = (token) => ({
  headers: { Authorization: `Bearer ${token}` }
});

const qs = (obj = {}) => {
  const params = new URLSearchParams();
  Object.entries(obj).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    if (Array.isArray(v)) v.forEach((item) => params.append(k, item));
    else params.set(k, v);
  });
  const s = params.toString();
  return s ? `?${s}` : '';
};

/* ========================================================================== */
/* FINANCE – NIT → GL mappings                                                 */
/* Base: /api/finance/nit-account-maps                                        */
/* ========================================================================== */
const NIT_API = `${apiMainUrl}finance/nit-account-maps`;

const searchNitAccountMaps = async (companyId, filter, token) => {
  const url = `${NIT_API}${qs({ companyId, ...filter })}`;
  const { data } = await axios.get(url, withAuth(token));
  return data;
};

const getNitAccountMap = async (id, token) => {
  const { data } = await axios.get(`${NIT_API}/${id}`, withAuth(token));
  return data;
};

const upsertNitAccountMap = async (companyId, dto, token) => {
  const { data } = await axios.post(`${NIT_API}/${companyId}`, dto, withAuth(token));
  return data;
};

const disableNitAccountMap = async (id, userEmail, token) => {
  await axios.post(`${NIT_API}/${id}/disable${qs({ userEmail })}`, null, withAuth(token));
};

/* ========================================================================== */
/* FINANCE – Account → costing-code mappings                                  */
/* Base: /api/finance/account-costing-code-maps                               */
/* ========================================================================== */
const ACCOUNT_COSTING_CODE_API = `${apiMainUrl}finance/account-costing-code-maps`;

const getCandidates = async (companyId, params, token) => {
  const url = `${ACCOUNT_COSTING_CODE_API}/candidates${qs({ companyId, ...params })}`;
  const { data } = await axios.get(url, withAuth(token));
  return data;
};

const upsertAccountCostingCodeMap = async (companyId, dto, token) => {
  const { data } = await axios.post(`${ACCOUNT_COSTING_CODE_API}/${companyId}`, dto, withAuth(token));
  return data;
};

/* ========================================================================== */
/* CONCILIATION – Workbench (Screen 3)                                        */
/* Base: /api/conciliation/workbench                                          */
/* ========================================================================== */
const WB_API = `${apiMainUrl}conciliation/workbench`;

// A) VendorPayments work
const getVendorPaymentsWork = async (companyId, filter, token) => {
  const url = `${WB_API}/vendor-payments${qs({ companyId, ...filter })}`;
  const { data } = await axios.get(url, withAuth(token));
  return data;
};

const bulkEditVendorPayments = async (companyId, edits, token) => {
  const url = `${WB_API}/vendor-payments/bulk-edit/${companyId}`;
  await axios.post(url, edits, withAuth(token));
};

const validateVendorPayments = async (companyId, workIds, token) => {
  const url = `${WB_API}/vendor-payments/validate/${companyId}`;
  const { data } = await axios.post(url, workIds, withAuth(token));
  return data;
};

// B) PurchaseInvoices work
const getPurchaseInvoicesWork = async (companyId, filter, token) => {
  const url = `${WB_API}/purchase-invoices${qs({ companyId, ...filter })}`;
  const { data } = await axios.get(url, withAuth(token));
  return data;
};

const bulkEditPurchaseInvoices = async (companyId, edits, token) => {
  const url = `${WB_API}/purchase-invoices/bulk-edit/${companyId}`;
  await axios.post(url, edits, withAuth(token));
};

const validatePurchaseInvoices = async (companyId, workIds, token) => {
  const url = `${WB_API}/purchase-invoices/validate/${companyId}`;
  const { data } = await axios.post(url, workIds, withAuth(token));
  return data;
};

// Files
const uploadInvoiceFile = async (file, fileNameHint, token) => {
  const formData = new FormData();
  formData.append('file', file);
  if (fileNameHint) formData.append('fileNameHint', fileNameHint);

  const { data } = await axios.post(`${WB_API}/files/upload`, formData, {
    ...withAuth(token),
    headers: {
      ...withAuth(token).headers,
      'Content-Type': 'multipart/form-data'
    }
  });
  return data; // Blob URL string
};

// Suggestions
const suggestFromNit = async (companyId, nit, token) => {
  const url = `${WB_API}/suggest/nit${qs({ companyId, nit })}`;
  const { data } = await axios.get(url, withAuth(token));
  return data; // AutoFillSuggestionDto | null
};

const suggestFromCard = async (companyId, last4, token) => {
  const url = `${WB_API}/suggest/card${qs({ companyId, last4 })}`;
  const { data } = await axios.get(url, withAuth(token));
  return data; // AutoFillSuggestionDto | null
};

/* ========================================================================== */
/* POSTING (batches)                                                          */
/* Base: /api/conciliation/posting                                            */
/* ========================================================================== */
const POSTING_API = `${apiMainUrl}conciliation/posting`;

const createPostingBatch = async (companyId, batchType, workEntityIds, requestedBy, token) => {
  const body = { CompanyId: companyId, BatchType: batchType, WorkEntityIds: workEntityIds, RequestedBy: requestedBy };
  const { data } = await axios.post(`${POSTING_API}/batches`, body, withAuth(token));
  return data; // Guid
};

const executePostingBatch = async (batchId, token) => {
  const { data } = await axios.post(`${POSTING_API}/batches/${batchId}/execute`, null, withAuth(token));
  return data; // BatchStatusDto
};

const getPostingBatch = async (batchId, token) => {
  const { data } = await axios.get(`${POSTING_API}/batches/${batchId}`, withAuth(token));
  return data; // BatchStatusDto
};

const listPostingBatches = async (companyId, filter, token) => {
  const url = `${POSTING_API}/batches${qs({ companyId, ...filter })}`;
  const { data } = await axios.get(url, withAuth(token));
  return data; // PagedResult<BatchListItemDto>
};

/* ========================================================================== */
/* REPORTS (Screens 4 & 5)                                                    */
/* Base: /api/conciliation/reports                                            */
/* ========================================================================== */
const RPT_API = `${apiMainUrl}conciliation/reports`;

const getUnmatchedInvoices = async (companyId, filter, token) => {
  const url = `${RPT_API}/unmatched-invoices${qs({ companyId, ...filter })}`;
  const { data } = await axios.get(url, withAuth(token));
  return data; // PagedResult<UnmatchedInvoiceRowDto>
};

const exportUnmatchedInvoices = async (companyId, filter, format, token) => {
  const url = `${RPT_API}/unmatched-invoices/export${qs({ companyId, ...filter, format })}`;
  const resp = await axios.get(url, { ...withAuth(token), responseType: 'blob' });
  return resp; // caller can handle save-as
};

const getMissingInfo = async (companyId, filter, token) => {
  const url = `${RPT_API}/missing-info${qs({ companyId, ...filter })}`;
  const { data } = await axios.get(url, withAuth(token));
  return data; // PagedResult<MissingInfoRowDto>
};

const exportMissingInfo = async (companyId, filter, format, token) => {
  const url = `${RPT_API}/missing-info/export${qs({ companyId, ...filter, format })}`;
  const resp = await axios.get(url, { ...withAuth(token), responseType: 'blob' });
  return resp; // caller can handle save-as
};

/* ========================================================================== */
/* MASTER DATA validation/lookup                                              */
/* Base: /api/masterdata                                                      */
/* ========================================================================== */
const MD_API = `${apiMainUrl}masterdata`;

const validateGlAccount = async (companyId, code, token) => {
  const url = `${MD_API}/validate/gl-account${qs({ companyId, code })}`;
  const { data } = await axios.get(url, withAuth(token));
  return data; // boolean
};

const validateCostCenter = async (companyId, code, token) => {
  const url = `${MD_API}/validate/cost-center${qs({ companyId, code })}`;
  const { data } = await axios.get(url, withAuth(token));
  return data; // boolean
};

const validateVendor = async (companyId, code, token) => {
  const url = `${MD_API}/validate/vendor${qs({ companyId, code })}`;
  const { data } = await axios.get(url, withAuth(token));
  return data; // boolean
};

const masterDataLookup = async (companyId, requestDto, token) => {
  const url = `${MD_API}/lookup${qs({ companyId })}`;
  const { data } = await axios.post(url, requestDto, withAuth(token));
  return data; // MasterDataLookupDto
};

/* ========================================================================== */
/* SAP MASTER DATA entities                                                   */
/* Base: /api/masterdata                                                      */
/* ========================================================================== */

const BASE = `${apiMainUrl}sap/master`;

const getBusinessPartnersWithNit = async (companyId, { search, page, pageSize }, token) => {
  const url = `${BASE}/business-partners-with-nit${qs({ companyId, search, page, pageSize })}`;
  const { data } = await axios.get(url, withAuth(token));
  return data;
};

const getGlAccounts = async (companyId, { search, page, pageSize }, token) => {
  const url = `${BASE}/gl-accounts${qs({ companyId, search, page, pageSize })}`;
  const { data } = await axios.get(url, withAuth(token));
  return data;
};

const getCostCenters = async (companyId, { search, page, pageSize }, token) => {
  const url = `${BASE}/cost-centers${qs({ companyId, search, page, pageSize })}`;
  const { data } = await axios.get(url, withAuth(token));
  return data; // { items: [{ centerCode, centerName }], totalItems }
};

const getDistributionRoutes = async (companyId, { search, page, pageSize }, token) => {
  const url = `${BASE}/distribution-routes${qs({ companyId, search, page, pageSize })}`;
  const { data } = await axios.get(url, withAuth(token));
  return data; // { items: [{ factorCode, factorDescription }], totalItems }
};

const getCostingDimensions = async (companyId, token) => {
  const url = `${BASE}/costing-dimensions${qs({ companyId })}`;
  const { data } = await axios.get(url, withAuth(token));
  return data;
};

const getCostingCodes = async (companyId, { search, page, pageSize } = {}, token) => {
  const url = `${BASE}/costing-codes${qs({ companyId, search, page, pageSize })}`;
  const { data } = await axios.get(url, withAuth(token));
  return data;
};

const getVendors = async (companyId, { search, page, pageSize }, token) => {
  const url = `${BASE}/vendors${qs({ companyId, search, page, pageSize })}`;
  const { data } = await axios.get(url, withAuth(token));
  return data;
};

/**
 * Response: [{ companyId: number, companyDb: string, baseUrl?: string }]
 */
const getCompanies = async (token) => {
  const url = `${BASE}/companies`;
  const { data } = await axios.get(url, withAuth(token));
  return data; // array
};

export const SapMasterApi = {
  getBusinessPartnersWithNit,
  getGlAccounts,
  getCostCenters,
  getDistributionRoutes,
  getCostingDimensions,
  getCostingCodes,
  getVendors,
  getCompanies
};

/* ========================================================================== */
/* EXPORTS                                                                    */
/* ========================================================================== */
export const NitAccountMapApi = {
  searchNitAccountMaps,
  getNitAccountMap,
  upsertNitAccountMap,
  disableNitAccountMap
};

export const AccountCostingCodeMapApi = {
  getCandidates,
  upsertAccountCostingCodeMap
};

export const WorkbenchApi = {
  getVendorPaymentsWork,
  bulkEditVendorPayments,
  validateVendorPayments,
  getPurchaseInvoicesWork,
  bulkEditPurchaseInvoices,
  validatePurchaseInvoices,
  uploadInvoiceFile,
  suggestFromNit,
  suggestFromCard
};

export const PostingApi = {
  createPostingBatch,
  executePostingBatch,
  getPostingBatch,
  listPostingBatches
};

export const ReportsApi = {
  getUnmatchedInvoices,
  exportUnmatchedInvoices,
  getMissingInfo,
  exportMissingInfo
};

export const MasterDataApi = {
  validateGlAccount,
  validateCostCenter,
  validateVendor,
  masterDataLookup
};

const CONCILIATION_API = `${apiMainUrl}conciliation`;
const WORKFLOW_API = `${apiMainUrl}conciliation/workflow`;

// Read conciliagastos
const getConciliationGastos = async (filter, token) => {
  const url = `${CONCILIATION_API}/gastos${qs(filter)}`;
  const { data } = await axios.get(url, withAuth(token));
  return data;
};

const getConciliationPagos = async (filter, token) => {
  const url = `${CONCILIATION_API}/pagos${qs(filter)}`;
  const { data } = await axios.get(url, withAuth(token));
  return data;
};

const getConciliationRel = async (filter, token) => {
  const url = `${CONCILIATION_API}/rel${qs(filter)}`;
  const { data } = await axios.get(url, withAuth(token));
  return data;
};

const exportConciliationCsv = async (type, filter, token) => {
  if (!['gastos', 'rel', 'pagos'].includes(type)) throw new Error('Invalid conciliation type');
  const { page, pageSize, ...exportFilter } = filter;
  const url = `${CONCILIATION_API}/${type}/export${qs(exportFilter)}`;
  const { data } = await axios.get(url, { ...withAuth(token), responseType: 'blob' });
  return data;
};

const getConciliationRelStats = async (filter, token) => {
  const url = `${CONCILIATION_API}/rel/stats${qs(filter)}`;
  const { data } = await axios.get(url, withAuth(token));
  return data;
};

/**
 * @returns {Promise<import('../Models/ConciliationGastosModels').ConciliationGastosDetailDto>}
 */
const getConciliationGastoByKey = async (conciliationKey, token) => {
  const { data } = await axios.get(`${CONCILIATION_API}/gastos/${encodeURIComponent(conciliationKey)}`, withAuth(token));
  return data;
};

/**
 * @param {'rel'|'gastos'|'pagos'} conciliationType
 * @param {string} conciliationKey
 * @param {{reason: string|null}} dto
 * @returns {Promise<import('../Models/IncorrectMatchModels').IncorrectMatchResponseDto>}
 */
const markIncorrectMatch = async (conciliationType, conciliationKey, dto, token) => {
  const { data } = await axios.post(
    `${CONCILIATION_API}/incorrect-matches/${conciliationType}/${encodeURIComponent(conciliationKey)}`,
    dto,
    withAuth(token)
  );
  return data;
};

const getIncorrectMatch = async (conciliationType, conciliationKey, token) => {
  const { data } = await axios.get(
    `${CONCILIATION_API}/incorrect-matches/${conciliationType}/${encodeURIComponent(conciliationKey)}`,
    withAuth(token)
  );
  return data;
};

// Workbench
const getOficialNoMatchWorkbench = async (conciliationKey, token) => {
  const { data } = await axios.get(`${CONCILIATION_API}/workbench/${encodeURIComponent(conciliationKey)}`, withAuth(token));
  return data;
};

const saveOficialNoMatchDecision = async (conciliationKey, dto, token) => {
  const { data } = await axios.post(
    `${CONCILIATION_API}/workbench/${encodeURIComponent(conciliationKey)}`,
    dto,
    withAuth(token)
  );
  return data;
};

const completeOficialNoMatchDecision = async (conciliationKey, dto, token) => {
  const { data } = await axios.post(
    `${CONCILIATION_API}/workbench/${encodeURIComponent(conciliationKey)}/complete`,
    dto,
    withAuth(token)
  );
  return data;
};

const completeOficialNoMatchDecisionBatch = async (dto, token) => {
  const { data } = await axios.post(
    `${CONCILIATION_API}/workbench/complete/batch`,
    dto,
    withAuth(token)
  );
  return data;
};

const getEntFinNoMatchWorkbench = async (conciliationKey, token) => {
  const { data } = await axios.get(
    `${CONCILIATION_API}/workbench/pagos/${encodeURIComponent(conciliationKey)}`,
    withAuth(token)
  );
  return data;
};

const saveEntFinNoMatchDecision = async (conciliationKey, dto, token) => {
  const { data } = await axios.post(
    `${CONCILIATION_API}/workbench/pagos/${encodeURIComponent(conciliationKey)}`,
    dto,
    withAuth(token)
  );
  return data;
};

const completeEntFinNoMatchDecision = async (conciliationKey, dto, token) => {
  const { data } = await axios.post(
    `${CONCILIATION_API}/workbench/pagos/${encodeURIComponent(conciliationKey)}/complete`,
    dto,
    withAuth(token)
  );
  return data;
};

/**
 * @returns {Promise<import('../Models/ConciliationRelModels').ConciliationRelWorkbenchResponseDto>}
 */
const getConciliaRelWorkbench = async (conciliationKey, token) => {
  const { data } = await axios.get(
    `${CONCILIATION_API}/workbench/rel/${encodeURIComponent(conciliationKey)}`,
    withAuth(token)
  );
  return data;
};

const saveConciliaRelDecision = async (conciliationKey, dto, token) => {
  const { data } = await axios.post(
    `${CONCILIATION_API}/workbench/rel/${encodeURIComponent(conciliationKey)}`,
    dto,
    withAuth(token)
  );
  return data;
};

const completeConciliaRelDecision = async (conciliationKey, dto, token) => {
  const { data } = await axios.post(
    `${CONCILIATION_API}/workbench/rel/${encodeURIComponent(conciliationKey)}/complete`,
    dto,
    withAuth(token)
  );
  return data;
};

const getConciliaRelActions = async (conciliationKey, token) => {
  const { data } = await axios.get(
    `${CONCILIATION_API}/workbench/rel/${encodeURIComponent(conciliationKey)}/actions`,
    withAuth(token)
  );
  return data;
};

const previewWorkbenchSuggestions = async (payload, token) => {
  const { data } = await axios.post(`${CONCILIATION_API}/workbench/suggestions/preview`, payload, withAuth(token));
  return data;
};

const startWorkbenchSuggestions = async (payload, token) => {
  const { data } = await axios.post(`${CONCILIATION_API}/workbench/suggestions/start`, payload, withAuth(token));
  return data;
};

const getWorkbenchSuggestionRun = async (runId, token) => {
  const { data } = await axios.get(
    `${CONCILIATION_API}/workbench/suggestions/${encodeURIComponent(runId)}`,
    withAuth(token)
  );
  return data;
};

const resumeWorkbenchSuggestionRun = async (runId, token) => {
  const { data } = await axios.post(
    `${CONCILIATION_API}/workbench/suggestions/${encodeURIComponent(runId)}/resume`,
    null,
    withAuth(token)
  );
  return data;
};

const listWorkbenchSuggestionRuns = async (filter, token) => {
  const url = `${CONCILIATION_API}/workbench/suggestions${qs(filter)}`;
  const { data } = await axios.get(url, withAuth(token));
  return data;
};

// Process
const processConciliationRecord = async (conciliationKey, token) => {
  const { data } = await axios.post(
    `${CONCILIATION_API}/process/${encodeURIComponent(conciliationKey)}`,
    null,
    withAuth(token)
  );
  return data;
};

const processConciliationBatch = async (conciliationKeys, token) => {
  const { data } = await axios.post(
    `${CONCILIATION_API}/process/batch`,
    { conciliationKeys },
    withAuth(token)
  );
  return data;
};

const processConciliationPagoRecord = async (conciliationKey, token) => {
  const { data } = await axios.post(
    `${CONCILIATION_API}/process/pagos/${encodeURIComponent(conciliationKey)}`,
    null,
    withAuth(token)
  );
  return data;
};

const processConciliationPagoBatch = async (conciliationKeys, token) => {
  const { data } = await axios.post(
    `${CONCILIATION_API}/process/pagos/batch`,
    { conciliationKeys },
    withAuth(token)
  );
  return data;
};

const processConciliationRelRecord = async (conciliationKey, token) => {
  const { data } = await axios.post(
    `${CONCILIATION_API}/process/rel/${encodeURIComponent(conciliationKey)}`,
    null,
    withAuth(token)
  );
  return data;
};

const processConciliationRelBatch = async (conciliationKeys, token) => {
  const { data } = await axios.post(
    `${CONCILIATION_API}/process/rel/batch`,
    { conciliationKeys },
    withAuth(token)
  );
  return data;
};

// Workflow history
const getWorkflowActionsByKey = async (conciliationKey, token) => {
  const { data } = await axios.get(
    `${WORKFLOW_API}/actions/by-key/${encodeURIComponent(conciliationKey)}`,
    withAuth(token)
  );
  return data;
};

export const ConciliationApi = {
  exportConciliationCsv,
  getConciliationGastos,
  getConciliationPagos,
  getConciliationRel,
  getConciliationRelStats,
  getConciliationGastoByKey,
  markIncorrectMatch,
  getIncorrectMatch,
  getOficialNoMatchWorkbench,
  saveOficialNoMatchDecision,
  completeOficialNoMatchDecision,
  completeOficialNoMatchDecisionBatch,
  getEntFinNoMatchWorkbench,
  saveEntFinNoMatchDecision,
  completeEntFinNoMatchDecision,
  getConciliaRelWorkbench,
  saveConciliaRelDecision,
  completeConciliaRelDecision,
  getConciliaRelActions,
  previewWorkbenchSuggestions,
  startWorkbenchSuggestions,
  getWorkbenchSuggestionRun,
  resumeWorkbenchSuggestionRun,
  listWorkbenchSuggestionRuns,
  processConciliationRecord,
  processConciliationPagoRecord,
  processConciliationRelRecord,
  getWorkflowActionsByKey,
  processConciliationBatch,
  processConciliationPagoBatch,
  processConciliationRelBatch
};

const CONCILIATION_REPORTS_API = `${apiMainUrl}conciliation/reports`;
const CONCILIATION_EXTRACTIONS_API = `${apiMainUrl}conciliation-extractions`;

const getConciliationGastosReport = async (filter, token) => {
  const url = `${CONCILIATION_REPORTS_API}/gastos${qs(filter)}`;
  const { data } = await axios.get(url, withAuth(token));
  return data;
};

const getConciliationPagosReport = async (filter, token) => {
  const url = `${CONCILIATION_REPORTS_API}/pagos${qs(filter)}`;
  const { data } = await axios.get(url, withAuth(token));
  return data;
};

export const ConciliationReportsApi = {
  getConciliationGastosReport,
  getConciliationPagosReport
};

const getConciliationExtractionsOverview = async (token) => {
  const { data } = await axios.get(`${CONCILIATION_EXTRACTIONS_API}/overview`, withAuth(token));
  return data;
};

const runConciliationSapExtraction = async (payload, token) => {
  const { data } = await axios.post(`${CONCILIATION_EXTRACTIONS_API}/sap/run`, payload, withAuth(token));
  return data;
};

const runConciliationSapCatalogsExtraction = async (payload, token) => {
  const { data } = await axios.post(`${CONCILIATION_EXTRACTIONS_API}/sap-catalogs/run`, payload, withAuth(token));
  return data;
};

const runConciliationBankStatementsExtraction = async (payload, token) => {
  const { data } = await axios.post(`${CONCILIATION_EXTRACTIONS_API}/bank-statements/run`, payload, withAuth(token));
  return data;
};

const runConcurSync = async (token) => {
  const { data } = await axios.post(`${apiMainUrl}ConcurSync/sync/company/1`, null, withAuth(token));
  return data;
};

export const ConciliationExtractionsApi = {
  getOverview: getConciliationExtractionsOverview,
  runSapExtraction: runConciliationSapExtraction,
  runSapCatalogsExtraction: runConciliationSapCatalogsExtraction,
  runBankStatementsExtraction: runConciliationBankStatementsExtraction,
  runConcurSync
};

/* ========================================================================== */
/* CONCUR - Manual expense report processing                                  */
/* Base: /api/concur/expense-processing                                       */
/* ========================================================================== */
const CONCUR_EXPENSE_PROCESSING_API = `${apiMainUrl}concur/expense-processing`;

/**
 * @param {{companyId?: number, search?: string, page: number, pageSize: number}} filter
 * @param {string} token
 * @returns {Promise<import('../Models/ConcurExpenseProcessingModels').ConcurExpenseReportPage>}
 */
const getConcurExpenseReports = async (filter, token) => {
  const url = `${CONCUR_EXPENSE_PROCESSING_API}/reports${qs(filter)}`;
  const { data } = await axios.get(url, withAuth(token));
  return data;
};

/**
 * @param {string[]} reportIds
 * @param {string} token
 * @returns {Promise<import('../Models/ConcurExpenseProcessingModels').ConcurReportsProcessResponse>}
 */
const processConcurExpenseReports = async (reportIds, token) => {
  const distinctReportIds = [...new Set(reportIds)];
  if (distinctReportIds.length < 1 || distinctReportIds.length > 100) {
    throw new RangeError('Concur processing requests require between 1 and 100 distinct report IDs.');
  }
  const { data } = await axios.post(
    `${CONCUR_EXPENSE_PROCESSING_API}/reports/process`,
    { reportIds: distinctReportIds },
    withAuth(token)
  );
  return data;
};

/**
 * @param {string} reportId
 * @param {string} token
 * @returns {Promise<import('../Models/ConcurExpenseProcessingModels').ConcurReportProcessResult>}
 */
const processConcurExpenseReport = async (reportId, token) => {
  const { data } = await axios.post(
    `${CONCUR_EXPENSE_PROCESSING_API}/reports/${encodeURIComponent(reportId)}`,
    null,
    withAuth(token)
  );
  return data;
};

/**
 * @param {import('../Models/ConcurExpenseProcessingModels').ConcurMonthlyProcessRequest} request
 * @param {string} token
 * @returns {Promise<import('../Models/ConcurExpenseProcessingModels').ConcurMonthlyProcessResponse>}
 */
const queueConcurMonthlyProcessing = async (request = {}, token) => {
  const { data } = await axios.post(
    `${CONCUR_EXPENSE_PROCESSING_API}/monthly${qs(request)}`,
    null,
    withAuth(token)
  );
  return data;
};

export const ConcurExpenseProcessingApi = {
  getReports: getConcurExpenseReports,
  processReports: processConcurExpenseReports,
  processReport: processConcurExpenseReport,
  queueMonthly: queueConcurMonthlyProcessing
};
